import i18n from 'i18next';
import toNumber from 'lodash/toNumber';
import { StateCreator } from 'zustand';

import { DashboardVehicle } from '../dashboardStore/types/DashboardVehicle';
import { UpdateVehicleParams } from './types/UpdateVehicleParams';
import { VehicleConfirmationState } from './types/VehicleConfirmationState';
import { VehicleConfirmationStoreDependencies } from './types/VehicleConfirmationStoreDependencies';
import { VehicleMake } from './types/VehicleMake';
import { VehicleModel } from './types/VehicleModel';
import { VehicleTrim } from './types/VehicleTrim';

export const vehicleConfirmationStore =
  ({
    useApiStore,
  }: VehicleConfirmationStoreDependencies): StateCreator<VehicleConfirmationState> =>
  (set) => ({
    fetchVehicleMakesFailedMessage: null,
    fetchVehicleModelsFailedMessage: null,
    fetchVehicleTrimsFailedMessage: null,
    makes: [],
    models: [],
    trims: [],
    updateVehicleFailedMessage: null,
    updateVehicleSuccess: false,

    fetchVehicleMakes: (year: string): Promise<void> => {
      set({
        fetchVehicleMakesFailedMessage: null,
        makes: [],
        models: [],
        trims: [],
      });

      if (!year) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<VehicleMake[]>(`/vehicles/years/${year}/makes`)
        .then((makes): void => {
          set({ makes });
        })
        .catch((): void => {
          set({
            fetchVehicleMakesFailedMessage: i18n.t(
              'errors.fetchVehicleMakes.defaultMessage',
            ),
          });
        });
    },
    fetchVehicleModels: (year: string, make: string): Promise<void> => {
      set({
        fetchVehicleModelsFailedMessage: null,
        models: [],
        trims: [],
      });

      if (!year || !make) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<VehicleModel[]>(`/vehicles/years/${year}/makes/${make}/models`)
        .then((models): void => {
          set({ models });
        })
        .catch((): void => {
          set({
            fetchVehicleModelsFailedMessage: i18n.t(
              'errors.fetchVehicleModels.defaultMessage',
            ),
          });
        });
    },
    fetchVehicleTrims: (
      year: string,
      make: string,
      model: string,
    ): Promise<void> => {
      set({ fetchVehicleTrimsFailedMessage: null, trims: [] });

      if (!year || !make || !model) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<VehicleTrim[]>(
          `/vehicles/years/${year}/makes/${make}/models/${model}/trims`,
        )
        .then((trims): void => {
          set({ trims });
        })
        .catch((): void => {
          set({
            fetchVehicleTrimsFailedMessage: i18n.t(
              'errors.fetchVehicleTrims.defaultMessage',
            ),
          });
        });
    },
    updateVehicle: (params: UpdateVehicleParams): Promise<void> => {
      if (!params.vehicleId) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      const {
        vehicle: { make, model, trim, trimId, year },
        vehicleId,
      } = params;

      return portalHttpRequest
        .put<DashboardVehicle>(`/2.0/vehicles/${vehicleId}`, {
          make,
          model,
          trim,
          trimId,
          year: toNumber(year),
        })
        .then((): void => {
          set({ updateVehicleSuccess: true });
        })
        .catch((): void => {
          set({
            updateVehicleFailedMessage: i18n.t(
              'errors.updateVehicle.defaultMessage',
            ),
          });
        });
    },
  });
